import { Card, CardContent, CardHeader, Divider, Table, TableBody, TableCell, TableHead, TableRow, Theme, colors } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { useState } from 'react';
import { OPERATOR, WhereConstraints } from '../../../graphql/common.datatypes';
import { formatDate, formatGraphQlDate, formatMoney } from '../../../utils/formatters';
import moment from 'moment';
import useRouter from 'use-react-router';
import PaymentCheckoutFrame from '../../Payments/PaymentCheckoutFrame';
import getMyBillingParties from './getMyBillingParties.api';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import { Page } from '../../../components/Page';
import { BillingParty } from '../../../graphql/billing_party.datatypes';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { PageHeader } from '../../../components/PageHeader';

const useStyles = makeStyles((theme: Theme) => ({
    root: {},
    content: {
        padding: 0
    },
    inner: {
        minWidth: 800
    },
    pastdue: {
        backgroundColor: colors.yellow[100],
        '&:hover': {
            backgroundColor: colors.yellow[200]
        }
    },
}));

const MyStay = (props: { className?: string, isAdmin: boolean }) => {
    const { className, isAdmin, } = props;
    const classes = useStyles();
    const [makePaymentPartyId, setMakePaymentPartyId] = useState(-1);
    const [makePaymentMemberId, setMakePaymentMemberId] = useState(-1);

    const getConstraintsFromFilters = () => {
        const AND: WhereConstraints[] = [];
        AND.push({
            column: 'disabled',
            operator: OPERATOR.EQ,
            value: false
        });
        return {
            AND,
        };
    };

    const hasGroupBooking: WhereConstraints = {
        AND: [{
            column: 'END_DATE',
            operator: OPERATOR.GTE,
            value: formatGraphQlDate(moment()),
        }]
    };

    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [page, setPage] = useState(1);
    const [orderBy] = useState([{ column: 'created_at', order: 'DESC' }]);
    const [where, setWhere] = useState(getConstraintsFromFilters());
    const billingPartiesData = getMyBillingParties(rowsPerPage, page, where, orderBy, hasGroupBooking);
    const myBillingParties = billingPartiesData && billingPartiesData.data && billingPartiesData.data.myBillingParties && billingPartiesData.data.myBillingParties.data;

    return (
        <React.Fragment>
            <LoadingOverlay loading={billingPartiesData.loading}>
                {myBillingParties && (
                    <React.Fragment>
                        <Page className={classes.root} title={'My Events'}>
                            <PageHeader section='' title={'My Events'} />
                            <p style={{ marginTop: 20 }}>
                                To view the "My Stay" page, please select the trip below that you would like to view. There you can download your invoice, make payment and manage that specific reservation.
                            </p>
                            <div
                                className={clsx(classes.root, className)}
                                style={{ marginTop: 20 }}
                            >
                                <Card>
                                    <CardHeader
                                        title={'Events'}
                                    />
                                    <Divider />
                                    <CardContent className={classes.content}>
                                        <PerfectScrollbar>
                                            <div className={classes.inner}>
                                                <TableData data={myBillingParties} isAdmin={isAdmin} />
                                            </div>
                                        </PerfectScrollbar>
                                    </CardContent>
                                </Card>
                            </div>
                        </Page>
                    </React.Fragment>
                )}
            </LoadingOverlay>

            {makePaymentPartyId !== -1 && makePaymentMemberId && (
                <PaymentCheckoutFrame
                    open={true}
                    billingPartyId={makePaymentPartyId}
                    billingPartyMemberId={makePaymentMemberId}
                    onClose={() => setMakePaymentPartyId(-1)}
                    onSuccess={() => setMakePaymentPartyId(-1)}
                />
            )}
        </React.Fragment>
    );
};

export default MyStay;

const TableData = (props: {
    data: BillingParty[];
    isAdmin: boolean;
}) => {
    const { data, isAdmin } = props;
    const { history } = useRouter();
    const classes = useStyles();
    const date = moment();

    const filteredData = data.filter(party => {
        return moment(party.booking!.end_date).diff(date, 'days') >= 0;
    });

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Group</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Primary Member</TableCell>
                    <TableCell style={{ width: 100 }}>Total</TableCell>
                    <TableCell style={{ width: 100 }}>Outstanding</TableCell>
                    <TableCell style={{ width: 100 }}>Past due</TableCell>
                    <TableCell style={{ width: 100 }}>Pending E-check</TableCell>
                    <TableCell style={{ width: 100 }}>Check In</TableCell>
                    <TableCell style={{ width: 100 }}>Check Out</TableCell>
                    <TableCell style={{ width: 100 }}># Travellers</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.map(party => {
                    const pimary = party.payingMembers && party.payingMembers.find(member => member.primary_paying_member);
                    const showPrices = isAdmin || (moment(party.booking!.end_date).add(15, 'days').diff(moment(), 'days') > 0);

                    return (
                        <TableRow
                            hover
                            selected={false}
                            key={party.id}
                            className={clsx({
                                [classes.pastdue]: party.overduePayments,
                            })}
                            onClick={() => {
                                history.push('/my_events/' + party.id)
                            }}
                        >
                            <TableCell>{party.booking!.group!.name}</TableCell>
                            <TableCell>{party.booking!.group!.tour!.display_name}</TableCell>
                            <TableCell>{pimary && (pimary.first_name + ' ' + pimary.last_name)}</TableCell>
                            <TableCell>{showPrices ? (formatMoney(party.cost!, true)) : 'n/a'}</TableCell>
                            <TableCell>{showPrices ? (formatMoney(party.outstandingBalance!, true)) : 'n/a'}</TableCell>
                            <TableCell>{showPrices ? (formatMoney(party.overduePayments!, true)) : 'n/a'}</TableCell>
                            <TableCell>{party.hasPendingECheckPayments ? 'Yes' : 'No'}</TableCell>
                            <TableCell>{party.booking && formatDate(party.booking.start_date)}</TableCell>
                            <TableCell>{party.booking && formatDate(party.booking.end_date)}</TableCell>
                            <TableCell>{party.members && party.members.length}</TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    );
};
